import { computed, Injectable, signal } from '@angular/core';
import { webDtos } from '@vectronic/openApi-admin';
import { webEnums } from '@vectronic/openApi-customer';
import { FixTypeMode } from '@vectronic/util';

const THREE_D_FIX_TYPES: webEnums.FixTypeSearch[] = [
  'THREE_D_FIX_NOT_VALIDATED',
  'THREE_D_FIX_VALIDATED',
];

export interface MapFilterRequest extends webDtos.PositionRequest {
  fixTypeMode?: FixTypeMode;
}

@Injectable({ providedIn: 'root' })
export class MapFilterService {
  private afterUTC = signal<string | undefined>(undefined);
  private beforeUTC = signal<string | undefined>(undefined);
  private lastX = signal<number | undefined>(50);
  private count = signal(0);
  private showInvalidPositions = signal(false);
  private fixTypeSearch = signal<webEnums.FixTypeSearch[] | undefined>(undefined);
  private deployedOnly = signal<boolean>(undefined);

  onFilterChanged({ afterUTC, beforeUTC, lastX, deployedOnly, fixTypeMode }: MapFilterRequest) {
    this.afterUTC.set(afterUTC);
    this.beforeUTC.set(beforeUTC);
    this.lastX.set(lastX);
    this.deployedOnly.set(deployedOnly);
    this.applyFixTypeMode(fixTypeMode);
    this.count.set(0);
  }

  private applyFixTypeMode(fixTypeMode: FixTypeMode | undefined) {
    if (fixTypeMode === undefined) return;
    this.showInvalidPositions.set(fixTypeMode !== 'HIDE_INVALID');
    this.fixTypeSearch.set(fixTypeMode === 'AT_LEAST_3D' ? THREE_D_FIX_TYPES : undefined);
  }

  onSingleEvent(acquisitionTime: string) {
    const aMinute = 1000 * 60;
    const afterUTC = new Date(new Date(acquisitionTime).getTime() - aMinute).toISOString();
    this.afterUTC.set(afterUTC);
    this.beforeUTC.set(undefined);
    this.deployedOnly.set(undefined);
    this.lastX.set(undefined);
    this.count.set(1);
    this.showInvalidPositions.set(false);
    this.fixTypeSearch.set(undefined);
  }

  onSingleItem() {
    this.afterUTC.set(undefined);
    this.beforeUTC.set(undefined);
    this.deployedOnly.set(undefined);
    this.lastX.set(undefined);
    this.showInvalidPositions.set(false);
    this.fixTypeSearch.set(undefined);
    this.count.set(0);
  }

  request = computed<webDtos.PositionRequest>(() => ({
    orderBy: 'acquisitionTime',
    orderDirection: 'desc',
    page: 0,
    count: this.count(),
    lastX: this.lastX(),
    afterUTC: this.afterUTC(),
    beforeUTC: this.beforeUTC(),
    showInvalidPositions: this.showInvalidPositions(),
    fixTypeSearch: this.fixTypeSearch(),
    deployedOnly: this.deployedOnly(),
    columnKeywordsList: [
      'idDevice',
      'acquisitionTime',
      'latitude',
      'longitude',
      'altitude',
      'mortalityStatus',
      'lightCondition',
    ],
  }));
}
